import React, {useEffect, useState, createContext, useContext, useReducer} from "react";
import firebase from "firebase/app";
import * as Sentry from "@sentry/browser";
import Promise from "bluebird";
import Cookies from "js-cookie";

const UserContext = createContext({});


const POSTAUTH_FAILURE_MSG = "The cookie exchange failed, signing in anonymously...";
const API_URL = "https://internal.hackernoon.com/auth";

const initialState = {
  uid: null,
  email: null,
  name: null,
  handle: null,
  avatar: null,
  bio: null,
  twitter: null,
  facebook: null,
  linkedin: null,
  github: null,
  paymentPointer: null,
  preferredCharity: null,
  credits: null,
  isAdmin: null,
  isEditor: null,
  isSponsor: null,
  isAuthor: null,
  isBrand: null,
  isReader: null,
  isTrusted: null,
  secretLab: false,
  type: null,
  colors: null,
  onCart: 0,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "reset":
      return initialState;
    case "set":
    case "setUser": {
      return {...state, ...action.payload, _fbUser: firebase.auth().currentUser};
    }
    default:
      return state;
  }
};

const fetchMongoProfile = async (uid, token) => {
   const profileApiUrl = "https://internal.hackernoon.com/profile";
  // const profileApiUrl = "http://localhost:8087/profiles";

  try {
    const res = await fetch(`${profileApiUrl}/usercontext?uid=${uid}&course=true`, 
      { 
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
      }
    );
    const data = await res.json();
    if (!res.ok || !data.ok || !data.body) {
      throw new Error("Mongo profile not found or malformed");
    }
    return data.body;
  } catch (err) {
    console.error("Error fetching Mongo profile:", err);
    return null;
  }
};

const UserContextProvider = ({children}) => {
  let [user, userDispatch] = useReducer(reducer, initialState);
  const [loadingAuthState, setLoadingAuthState] = useState(true);
  const [isOnboarding, setIsOnboarding] = useState(false);

  const db = firebase.firestore();

  const setUser = (keyOrObj, val) => {
    if (keyOrObj && val) {
      userDispatch({ type: "set", payload: { [keyOrObj]: val } });
    } else if (keyOrObj && typeof keyOrObj === "object") {
      userDispatch({ type: "set", payload: keyOrObj });
    } else {
      throw new Error("Invalid parameters for setUser");
    }
  };

  const setUserData = async (authUser, createProfile = null) => {
    if (!authUser?.uid || authUser.id) return;
    const id = authUser?.uid || authUser.id;
    const token = await authUser.getIdToken(true);

    await fetch(`${API_URL}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken: token }),
    });

    let profileDocData; 

    if(createProfile){
      profileDocData = createProfile;
    }else{
      const profileUser = await fetchMongoProfile(id, token);
      profileDocData = profileUser || {};
    }

    const avatar = profileDocData?.avatar ?? authUser?.photoURL ?? `https://hackernoon.com/avatars/${authUser?.uid}.png`;

    const userData = {
      ...profileDocData,
      avatar,
      uid: authUser?.uid || profileDocData.id,
      noAuth: false
    };
    setUser(userData);

    setLoadingAuthState(false);
  };


  useEffect(() => {
    // a note on `user.isAnonymous === undefined`. I know this is weird,
    // but it's because we don't define it in the initial state, and this is the only case we want to target
    if (!loadingAuthState && user.uid && user.isAnonymous === undefined) {
      setIsOnboarding(!user.handle);
    }
  }, [user, loadingAuthState]);

  const tryPostAuth = async () => {
    try {
      const hasAuthCookie = Cookies.get("hasAuthCookie", { domain: ".hackernoon.com" });
      if (!hasAuthCookie) {
        setLoadingAuthState(false);
        return;
      }

      const postAuthRes = await fetch(`${API_URL}/postauth`, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!postAuthRes.ok) throw new Error(POSTAUTH_FAILURE_MSG);
      const { token } = await postAuthRes.json();
      if (!token) throw new Error(POSTAUTH_FAILURE_MSG);

      const result = await firebase.auth().signInWithCustomToken(token);
      const authUser = result?.user;

      if (authUser?.uid) {
        await setUserData(authUser);
      } else {
        setLoadingAuthState(false);
      }
    } catch (err) {
      console.error("tryPostAuth error:", err);
      userDispatch({ type: "reset" });
      setLoadingAuthState(false);
    }
  };

  const handleLogout = async () => {
    await fetch("https://bridge.hackernoon.com/auth/logout", {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    await firebase
      .auth()
      .signOut()
      .then(() => userDispatch({ type: "reset" }));
  };

  useEffect(() => {
    firebase.auth().onAuthStateChanged(async user => {
      if (user && !user.isAnonymous) {
        setUserData(user);
        const token = await user.getIdToken(true);

        await fetch(`${API_URL}`, {
          method: "POST",
          mode: "cors",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({idToken: token}),
        });
      } else if (!user) {
        await tryPostAuth();
      }
    });
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        authenticated: !!user?.uid && !!user?.email,
        getCurrentUser: () => firebase.auth().currentUser,
        handleLogout,
        userDispatch,
        setUserData,
        loadingAuthState,
        isOnboarding,
        setIsOnboarding,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

let UserContextConsumer = UserContext.Consumer;

const useUser = () => useContext(UserContext);

const authenticate = async idToken => {
  await fetch(`${API_URL}`, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({idToken: idToken}),
  });
};

export {authenticate, UserContext, UserContextProvider, UserContextConsumer, useUser};
